import { match, P } from "ts-pattern";

import {
  NateraIntakeFaRead,
  type NateraIntakePacketInput,
} from "@medbillai/generated-graphql-types";

import { projectedTargetCents } from "@medbillai/schemas";
import { isNateraExpressArm } from "@medbillai/schemas/nateraIntakePacket";

import { personaFor } from "./persona";
import type {
  BillData,
  EmotionalData,
  ExpressArmData,
  ExpressServedArm,
  FaReadData,
  HouseholdData,
  IncomeData,
  LandingEntryData,
  PlaybookArmData,
  ProgramData,
  StoryArmData,
  StoryData,
} from "./types";

export type ControlPacketAnswers = BillData &
  Partial<ProgramData> &
  EmotionalData &
  PlaybookArmData &
  StoryArmData &
  ExpressArmData &
  LandingEntryData &
  HouseholdData &
  IncomeData &
  FaReadData &
  StoryData;

type ExpressPacketAnswers = BillData &
  LandingEntryData &
  ProgramData & { expressArm: ExpressServedArm };

export type PacketAnswers = ControlPacketAnswers | ExpressPacketAnswers;

const isExpressPacket = (
  answers: PacketAnswers,
): answers is ExpressPacketAnswers => isNateraExpressArm(answers.expressArm);

/** Every key spelled out, so a field added to the input fails to build until it is sent. */
type BuiltPacket = Required<
  Omit<
    NateraIntakePacketInput,
    "accountCopyVariant" | "insuranceCarrier" | "insuranceStatus"
  >
>;

/**
 * `fa-not-pursued` is a declined income, which the packet already records on its own
 * field. It maps to UNLIKELY_QUALIFY because the enum's only downstream job is gating
 * the income-document activation item, and a case with no income to verify doesn't want
 * one. Mapping it to LIKELY_QUALIFY would be rejected by the server's schema anyway.
 */
const toFaReadEnum = (faRead: FaReadData["faRead"]): NateraIntakeFaRead =>
  match(faRead)
    .with(
      { kind: P.union("likely-qualifies", "likely-qualifies-unpriced") },
      () => NateraIntakeFaRead.LIKELY_QUALIFY,
    )
    .with(
      { kind: "unlikely-to-qualify" },
      () => NateraIntakeFaRead.UNLIKELY_QUALIFY,
    )
    .with(
      { kind: "negotiation-unavailable" },
      () => NateraIntakeFaRead.NEGOTIATION_UNAVAILABLE,
    )
    .with({ kind: "fa-not-pursued" }, () => NateraIntakeFaRead.UNLIKELY_QUALIFY)
    .exhaustive();

export const faReadAnswerFor = (faRead: FaReadData["faRead"]) => ({
  faRead: toFaReadEnum(faRead),
  projectedTargetCents: projectedTargetCents(faRead),
});

/** A refusal never carries a figure. */
export const incomeAnswerFor = (income: IncomeData["income"]) =>
  income.kind === "declared"
    ? {
        annualHouseholdIncomeCents: income.annualHouseholdIncomeCents,
        incomeDeclined: false,
        incomeRangeFloorCents: income.range?.floorCents ?? null,
        incomeRangeCeilingCents: income.range?.ceilingCents ?? null,
      }
    : {
        annualHouseholdIncomeCents: null,
        incomeDeclined: true,
        incomeRangeFloorCents: null,
        incomeRangeCeilingCents: null,
      };

/**
 * Assembles the wire packet from the answers the flow accumulated.
 *
 * Every skippable answer is sent as an explicit null rather than omitted: the warehouse
 * reads this with plain field access, where an absent key is indistinguishable from a
 * question we never asked. The persona rides along derived rather than stored — it is a
 * pure function of the two answers beside it, so the packet can't disagree with itself.
 */
export const buildNateraIntakePacket = (
  answers: PacketAnswers,
): BuiltPacket => {
  if (isExpressPacket(answers)) {
    return {
      billAmountCents: null,
      billRangeFloorCents: answers.billBand?.floorCents ?? null,
      billRangeCeilingCents: answers.billBand?.ceilingCents ?? null,
      billCount: answers.billCount,
      annualHouseholdIncomeCents: null,
      incomeDeclined: null,
      incomeRangeFloorCents: null,
      incomeRangeCeilingCents: null,
      householdSize: null,
      faRead: null,
      projectedTargetCents: null,
      stressLevel: null,
      feeling: null,
      persona: null,
      story: null,
      playbookArm: null,
      storyArm: null,
      expressArm: answers.expressArm,
      landingEntry: answers.landingEntry ?? null,
      program: answers.program,
    };
  }
  const declined = answers.income.kind === "declined";

  return {
    // Always null from this client; the field survives for packets that predate the bands.
    billAmountCents: null,
    billRangeFloorCents: answers.billBand?.floorCents ?? null,
    billRangeCeilingCents: answers.billBand?.ceilingCents ?? null,
    billCount: answers.billCount,
    annualHouseholdIncomeCents:
      answers.income.kind === "declared"
        ? answers.income.annualHouseholdIncomeCents
        : null,
    incomeDeclined: declined,
    incomeRangeFloorCents:
      answers.income.kind === "declared"
        ? (answers.income.range?.floorCents ?? null)
        : null,
    incomeRangeCeilingCents:
      answers.income.kind === "declared"
        ? (answers.income.range?.ceilingCents ?? null)
        : null,
    householdSize: answers.householdSize,
    // Off the same read the result screen rendered, so the member is quoted the
    // figure they saw rather than a recompute.
    ...faReadAnswerFor(answers.faRead),
    stressLevel: answers.stressLevel,
    feeling: answers.feeling,
    persona: personaFor(answers.stressLevel, answers.feeling),
    story: answers.story,
    // Null for a member never enrolled, so the case reads unenrolled rather than control.
    playbookArm: answers.playbookArm ?? null,
    storyArm: answers.storyArm ?? null,
    expressArm: answers.expressArm ?? null,
    landingEntry: answers.landingEntry ?? null,
    program: answers.program ?? "natera",
  };
};
